import { useRef, useState } from "react";
import axios from "axios";
import { Upload, Link as LinkIcon, X, Image as ImageIcon, Crop as CropIcon, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import ImageCropperModal from "./ImageCropperModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * <ImageUploadField />
 *
 * Reusable image input that accepts EITHER:
 *   • a file upload from device/camera (mobile capture supported) —
 *     stored as a base64 data URI in `value` (legacy mode) OR uploaded to
 *     Emergent object storage and returned as a URL (when `hosted` is true).
 *   • a URL (Unsplash, CDN, https://…)
 *
 * Returns a string in `value`:
 *   – `data:image/jpeg;base64,…` (when `hosted={false}`, kept for back-compat)
 *   – `/api/files/opikjewels/public/…` (when `hosted={true}`)
 *   – `https://…` (when user pastes a URL)
 *
 * Props:
 *   label          — field label
 *   value          — current string (data URI or URL)
 *   onChange       — receives the new string
 *   maxMB          — max file size in MB (default 5)
 *   accept         — accept attr (default "image/*")
 *   testid         — test id base
 *   helper         — helper text under the field
 *   cropAspect     — if set, shows "Crop" button that opens a fixed-aspect crop modal
 *   autoCropOnUpload — if true and cropAspect is set, opens cropper immediately after upload
 *   hosted         — if true, uploads via POST /api/files/upload and stores the returned URL
 *   uploadTag      — grouping tag sent with the upload (e.g. "warranty-photo")
 *   visibility     — "public" (default) | "private"
 */
const ImageUploadField = ({
  label,
  value = "",
  onChange,
  maxMB = 5,
  accept = "image/*",
  testid,
  helper,
  cropAspect = null,
  autoCropOnUpload = false,
  hosted = false,
  uploadTag = "misc",
  visibility = "public",
}) => {
  const fileRef = useRef(null);
  const [mode, setMode] = useState(() => (value && value.startsWith("data:") ? "file" : "url"));
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);

  const dataUriToBlob = (uri) => {
    const [header, b64] = uri.split(",");
    const mime = /data:(.*?);base64/.exec(header)?.[1] || "image/jpeg";
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  };

  const uploadHosted = async (blob, filename) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename || "upload.jpg");
      fd.append("visibility", visibility);
      fd.append("tag", uploadTag);
      const { data } = await axios.post(`${BACKEND}/api/files/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded to secure storage.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(String(msg));
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`Image must be under ${maxMB} MB.`);
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUri = ev.target.result;
      // If cropping is configured, open the cropper first — the cropped
      // dataURI is what we'll hand off (either to hosted upload or to onChange).
      if (cropAspect && autoCropOnUpload) {
        onChange(dataUri);
        setUploading(false);
        setCropOpen(true);
        toast.success("Image ready — crop to card fit.");
        return;
      }
      if (hosted) {
        await uploadHosted(file, file.name);
      } else {
        onChange(dataUri);
        toast.success("Image attached.");
        setUploading(false);
      }
    };
    reader.onerror = () => { setUploading(false); toast.error("Could not read file."); };
    reader.readAsDataURL(file);
  };

  const clear = () => {
    onChange("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const hasImage = !!value;
  const isDataUri = hasImage && value.startsWith("data:");

  return (
    <div>
      <label className="text-white/50 text-xs tracking-[0.18em] font-accent uppercase mb-2 block">
        {label}
      </label>

      {/* Mode toggle */}
      <div className="flex gap-1 mb-3 text-xs">
        <button type="button" onClick={() => setMode("file")}
          data-testid={`${testid}-mode-file`}
          className={`font-accent text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 border ${
            mode === "file" ? "border-gold text-gold" : "border-white/15 text-white/40 hover:text-white"
          }`}>
          <Upload size={11} className="inline mr-1.5 -mt-0.5" /> Upload {hosted ? <CloudUpload size={11} className="inline ml-1" /> : null}
        </button>
        <button type="button" onClick={() => setMode("url")}
          data-testid={`${testid}-mode-url`}
          className={`font-accent text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 border ${
            mode === "url" ? "border-gold text-gold" : "border-white/15 text-white/40 hover:text-white"
          }`}>
          <LinkIcon size={11} className="inline mr-1.5 -mt-0.5" /> Paste URL
        </button>
      </div>

      {/* File picker */}
      {mode === "file" && (
        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            data-testid={`${testid}-file`}
            type="file"
            accept={accept}
            onChange={handleFile}
            className="block w-full text-sm text-white/70 file:mr-4 file:py-2.5 file:px-5 file:border-0 file:bg-gold file:text-black file:font-accent file:uppercase file:tracking-[0.18em] file:text-[10px] file:cursor-pointer hover:file:bg-ivory"
          />
        </div>
      )}

      {/* URL field */}
      {mode === "url" && (
        <input
          data-testid={`${testid}-url`}
          type="url"
          value={isDataUri ? "" : value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="w-full bg-transparent border border-white/20 px-3 py-3 text-white focus:outline-none focus:border-gold"
        />
      )}

      {/* Preview */}
      {hasImage && !uploading && (
        <div className="mt-3 flex items-center gap-3">
          <div className="w-16 h-16 border border-white/15 bg-black overflow-hidden flex items-center justify-center relative">
            <img
              src={value.startsWith("/api/") ? `${BACKEND}${value}` : value}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <ImageIcon size={14} className="text-white/30 absolute" />
          </div>
          <div className="flex-1 text-xs text-white/50 font-accent tracking-[0.12em] uppercase truncate">
            {isDataUri ? "Local file attached" : value}
          </div>
          {cropAspect && (
            <button type="button" onClick={() => setCropOpen(true)} data-testid={`${testid}-crop`}
              className="text-white/70 hover:text-gold border border-white/20 hover:border-gold px-3 py-1.5 font-accent text-[10px] uppercase tracking-[0.18em] flex items-center gap-1"
              title="Crop to card fit">
              <CropIcon size={11} /> Crop
            </button>
          )}
          <button type="button" onClick={clear} data-testid={`${testid}-clear`}
            className="text-white/50 hover:text-red-400 p-2" title="Remove">
            <X size={14} />
          </button>
        </div>
      )}

      {uploading && (
        <div className="mt-3 text-xs text-gold font-accent tracking-[0.18em] uppercase">
          {hosted ? "Uploading to storage…" : "Reading file…"}
        </div>
      )}

      {helper && <div className="mt-2 text-[11px] text-white/40 italic">{helper}</div>}

      {cropOpen && cropAspect && hasImage && (
        <ImageCropperModal
          src={value.startsWith("/api/") ? `${BACKEND}${value}` : value}
          aspect={cropAspect}
          onCancel={() => setCropOpen(false)}
          onSave={async (dataUri) => {
            setCropOpen(false);
            if (hosted) {
              // Turn the cropped dataURI back into a blob and upload it.
              await uploadHosted(dataUriToBlob(dataUri), `crop-${Date.now()}.jpg`);
            } else {
              onChange(dataUri);
              toast.success("Photo cropped to card fit.");
            }
          }}
        />
      )}
    </div>
  );
};

export default ImageUploadField;
